import React, { memo, useEffect, useMemo, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import {
  FormErrorMessage,
  Text,
  Textarea,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Button,
  Box,
} from '@chakra-ui/core'
import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import useLocaleText from 'components/useLocaleText'
import { SquareChartGantt, ChevronDown, Bot, Edit3 } from 'lucide-react'
import { getNestedError } from '../utils'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { RootState } from 'constants/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { getPrompt, getPrompts } from 'redux/actions/promts'

interface AIPromptProps {
  keyName: string
  label: string
  placeholder: string
  enableValidation?: boolean
}

export const AIPrompt: React.FC<AIPromptProps> = memo(({ keyName, label, placeholder, enableValidation = false }) => {
  const { register, errors, setValue, watch, control, clearError } = useFormContext()
  const dispatch: AppDispatch = useDispatch()
  const { prompts, isLoading } = useSelector((state: RootState) => state.prompts)
  const [isCustomMode, setIsCustomMode] = useState(true)
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false)
  const [selectedPromptText, setSelectedPromptText] = useState<string | null>(null)

  const t = useLocaleText(label, true)
  const t_placeholder = useLocaleText(placeholder, true)
  const error_prompt = useLocaleText('error_prompt', true)
  const select_prompt = useLocaleText('select_prompt', true)
  const custom_prompt = useLocaleText('custom_prompt', true)
  const selected_prompt = useLocaleText('selected_prompt', true)
  const loading_prompt = useLocaleText('loading_prompt', true)
  const loading_prompts = useLocaleText('loading_prompts', true)

  const error = getNestedError(errors, keyName)
  const selectedPromptId = useMemo(() => watch('data.prompt_id'), [watch])
  const promptText = watch(keyName)
  const selectedPrompt = useMemo(() => prompts.find((p) => p.oid === selectedPromptId), [prompts, selectedPromptId])

  useEffect(() => {
    dispatch(getPrompts())
  }, [dispatch])

  useEffect(() => {
    if (selectedPromptId && prompts.length > 0 && !isLoading) {
      const foundPrompt = prompts.find((p) => p.oid === selectedPromptId)
      if (foundPrompt) {
        setIsCustomMode(false)
        setSelectedPromptText(promptText)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromptId, prompts, isLoading])

  const validationRules = useMemo(
    () => ({
      validate: (value: string) => {
        if (!enableValidation) return true

        if (isCustomMode) {
          return value?.trim() ? true : error_prompt
        }

        return selectedPromptId ? true : error_prompt
      },
    }),
    [enableValidation, error_prompt, isCustomMode, selectedPromptId]
  )

  const handlePromptSelect = async (promtId: string) => {
    try {
      setIsLoadingPrompt(true)
      setValue('data.prompt_id', promtId)
      const promptText = await dispatch(getPrompt({ prompt_id: promtId }))
      setSelectedPromptText(promptText)
      setValue(keyName, '')
      setIsCustomMode(false)
      clearError(keyName)
    } catch (error) {
      console.error('Failed to select prompt:', error)
    } finally {
      setIsLoadingPrompt(false)
    }
  }

  const handleCustomMode = () => {
    setIsCustomMode(true)
    setValue('data.prompt_id', '')
    setValue(keyName, '')
  }

  const currentPromptText = isCustomMode ? promptText : selectedPromptText || ''

  return (
    <StyledFormControl isInvalid={!!error}>
      <Controller name="data.prompt_id" control={control} defaultValue="" as={<input type="hidden" />} />

      <StyledFormLabel required={enableValidation}>
        <SquareChartGantt size={18} strokeWidth={2.5} />
        <Text>{t}</Text>
      </StyledFormLabel>

      <Flex mb={3} alignItems="center" style={{ gap: '10px' }}>
        <Menu>
          <MenuButton
            as={NoFocusButton}
            size="sm"
            pointerEvents={isLoading ? 'none' : 'auto'}
            opacity={isLoading ? 0.8 : 1}
            borderRadius="5px"
            cursor="pointer"
          >
            <Flex alignItems="center" style={{ gap: '8px' }}>
              <Bot size={16} />
              <Text>{isLoading ? loading_prompts : selectedPrompt?.title || select_prompt}</Text>
              {!isLoading && <ChevronDown size={16} />}
            </Flex>
          </MenuButton>
          <MenuList maxH="200px" overflowY="auto">
            {prompts.map(({ oid, title, content }) => (
              <MenuItem
                key={oid}
                onClick={() => handlePromptSelect(oid)}
                bg={selectedPromptId === oid ? 'blue.50' : 'white'}
              >
                <Box>
                  <Text fontWeight="medium" fontSize="sm">
                    {title}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mt={1} isTruncated>
                    {content}
                  </Text>
                </Box>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <Button
          variantColor={isCustomMode ? 'blue' : 'gray'}
          size="sm"
          onClick={handleCustomMode}
          isDisabled={isLoading}
          opacity={isLoading ? 0.5 : 1}
          cursor={isLoading ? 'not-allowed' : 'pointer'}
          _focus={{ boxShadow: 'none' }}
          _active={{ boxShadow: 'none' }}
        >
          <Flex alignItems="center" style={{ gap: '8px' }}>
            <Edit3 size={16} />
            <Text>{custom_prompt}</Text>
          </Flex>
        </Button>
      </Flex>

      {!isCustomMode && selectedPromptId && (
        <Box bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200" mb={3}>
          <Flex alignItems="center" mb={2}>
            <Bot size={16} />
            <Text ml={2} fontWeight="medium" fontSize="sm" color="blue.700">
              {selected_prompt}: {selectedPrompt?.title}
            </Text>
          </Flex>
        </Box>
      )}

      <Textarea
        name={keyName}
        ref={register(validationRules)}
        value={isLoadingPrompt ? loading_prompt : currentPromptText}
        onChange={(e) => {
          if (isCustomMode) {
            setValue(keyName, (e.target as HTMLTextAreaElement).value)
          }
        }}
        minH={{ base: '120px', md: '150px' }}
        borderRadius="5px"
        bg={!isCustomMode || isLoading ? 'gray.50' : 'white'}
        resize="none"
        border="1px solid"
        placeholder={isCustomMode ? t_placeholder : ''}
        isReadOnly={!isCustomMode || isLoading}
        cursor={!isCustomMode || isLoading ? 'not-allowed' : 'text'}
        opacity={isLoading ? 0.7 : 1}
        fontStyle={isLoading ? 'italic' : 'normal'}
        color={isLoading ? 'gray.500' : 'inherit'}
        textAlign={isLoading ? 'center' : 'inherit'}
        _focus={{
          boxShadow: isCustomMode || isLoading ? '0px 0px 0px 1px black' : 'none',
          borderColor: isCustomMode || isLoading ? 'black' : 'gray.300',
        }}
        _hover={{
          borderColor: isCustomMode || isLoading ? 'gray.400' : 'gray.300',
        }}
      />

      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </StyledFormControl>
  )
})
